import React, { useMemo, useCallback, useState, useEffect } from 'react';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridColumnGroupingModel,
} from '@mui/x-data-grid';
import { MuiButton, MuiRadio, MuiDrawer } from '@ace/ace-ui-web';
import PerfYardRxSkeletonDataGrid from '../../../../../components/skeletons/performance/ecommerce/PerfYardRxSkeletonDataGrid';
import pagesStandardStyles from '../../../../Pages.module.css';
import yardRxStyles from './YardRx.module.css';
import { getDataGridStyles } from '../../../../GridStyles';
import { getGroupColumnHeaderStyles } from '../../../../GroupColumnHeaderGridStyles';
import {
  formatNumberWithCommas,
  formatLoadDate,
  isValidApiDate,
  isMobileDevice,
  getAlternatingRowClassName,
} from '../../../../../utils/appUtils';
import { YardRXModel } from '../../../../../models/YardRXModel';
import { kpiContentMap } from '../../../../../utils/viewInfoUtils';
import { useAppSelector } from '../../../../../redux/hooks';
import {
  selectYardRx,
  selectYardRxLoading,
} from '../../../../../redux/PerformanceReducer/Ecommerce/yardRxSlice';
import { useBuildDynamicUrl } from '../../../../../hooks/useDynamicUrl';
import { useClickHandler } from '../../../../../utils/usageMetricsLogger';
import lang from '../../../../../languages/en.json';

// ─── Props interface ─────────────────────────────────────────────────────────

interface YardRxProps {
  /** Pre-fetched data for print view — skips Redux read and API fetch when provided */
  data?: YardRXModel | null;
  /** When true, hides interactive elements (radio buttons, drawer, report buttons) */
  isPrintView?: boolean;
}

// Row interface for DataGrid
const PAGE_NM = 'Performance';
const TAB_NM = 'Ecommerce';
const SECTION_NM = 'Yard RX';
const MOBILE_BREAKPOINT = 850;

interface GridRow {
  id: number;
  fixedColumn: string;
  dataKey:
    | 'subscriptions'
    | 'projectedSalesWithAddOns'
    | 'projectedSalesWithoutAddOns'
    | 'projectInstantSavings'
    | 'projectedUnitsWithAddOns'
    | 'projectedUnitsWithoutAddOns';
}

// Helper function to format cells
const renderFormatCell = (
  params: GridRenderCellParams<GridRow, number | null | undefined>,
): JSX.Element | string => {
  if (params.value === null || params.value === undefined) {
    return <span className={pagesStandardStyles.centeredDash}>-</span>;
  }

  // Add $ for sales and instant savings rows
  const fixedColumnLabel = params.row.fixedColumn;
  if (fixedColumnLabel.includes('Sales') || fixedColumnLabel.includes('Savings')) {
    return `$${formatNumberWithCommas(params.value.toString(), true)}`;
  } else {
    return formatNumberWithCommas(params.value.toString());
  }
};

// Helper function to create column group
function createColumnGroup(
  groupName: 'cumulativeDetails' | 'ytdDetails',
  data: YardRXModel | null,
): GridColDef[] {
  const columnConfig = {
    minWidth: 115.2,
    flex: 1,
  };

  return [
    {
      field: groupName,
      headerName: '',
      ...columnConfig,
      headerClassName: pagesStandardStyles.columnHeader,
      align: 'right',
      valueGetter: (_, gridRow) => data?.[groupName]?.[gridRow.dataKey],
      renderCell: renderFormatCell,
    },
  ];
}

const YardRx: React.FC<YardRxProps> = ({ data: dataProp, isPrintView = false }) => {
  const reduxData = useAppSelector(selectYardRx);
  const reduxLoading = useAppSelector(selectYardRxLoading);
  const buildDynamicUrl = useBuildDynamicUrl();
  const handleAndLogClick = useClickHandler(PAGE_NM, TAB_NM);

  // Use prop data (print view) or Redux data (live view)
  const yardRxData = dataProp ?? reduxData;
  const isLoading = isPrintView ? false : reduxLoading;

  const [selectedView, setSelectedView] = useState<'order' | 'sku'>('order');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerContent, setDrawerContent] = useState<{
    title: string;
    content: React.ReactNode;
  }>({
    title: '',
    content: null,
  });

  // Check mobile device once for conditional UI element rendering
  const isMobileDeviceCheck = isPrintView ? false : isMobileDevice();

  // Track mobile state for responsive fixed column width (250px desktop, 150px mobile at MOBILE_BREAKPOINT)
  const [isMobile, setIsMobile] = useState(
    isPrintView ? false : window.innerWidth <= MOBILE_BREAKPOINT,
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handler for Next 90 Day Delivery Report button (must stay inside - uses state)
  const handleNextDeliveryReport = useCallback(() => {
    let url = '';
    if (selectedView === 'order') {
      url = buildDynamicUrl('WFC', 'YDND', 'order');
    } else {
      url = buildDynamicUrl('WFC', 'YDND', 'sku');
    }
    if (url) {
      handleAndLogClick(`${SECTION_NM} - Next 90 Day Delivery Report`, {
        url,
        urlTarget: '_blank',
      });
    }
  }, [selectedView, buildDynamicUrl, handleAndLogClick]);

  // Create rows for the grid
  const rowData = useMemo(
    () => [
      {
        id: 1,
        fixedColumn: lang.yardRxContent.rowSubscriptions,
        dataKey: 'subscriptions' as const,
      },
      {
        id: 2,
        fixedColumn: lang.yardRxContent.rowSalesWithAddOns,
        dataKey: 'projectedSalesWithAddOns' as const,
      },
      {
        id: 3,
        fixedColumn: lang.yardRxContent.rowSalesWithoutAddOns,
        dataKey: 'projectedSalesWithoutAddOns' as const,
      },
      {
        id: 4,
        fixedColumn: lang.yardRxContent.rowInstantSavings,
        dataKey: 'projectInstantSavings' as const,
      },
      {
        id: 5,
        fixedColumn: lang.yardRxContent.rowUnitsWithAddOns,
        dataKey: 'projectedUnitsWithAddOns' as const,
      },
      {
        id: 6,
        fixedColumn: lang.yardRxContent.rowUnitsWithoutAddOns,
        dataKey: 'projectedUnitsWithoutAddOns' as const,
      },
    ],
    [],
  );

  // Create column groups
  const cumulativeColumns = useMemo(
    () => createColumnGroup('cumulativeDetails', yardRxData),
    [yardRxData],
  );
  const ytdColumns = useMemo(() => createColumnGroup('ytdDetails', yardRxData), [yardRxData]);

  // Get last column field names for group styling
  const lastGroupOneField = useMemo(
    () => cumulativeColumns[cumulativeColumns.length - 1].field,
    [cumulativeColumns],
  );

  // Combine all columns
  const columns: GridColDef[] = useMemo(
    () => [
      // Fixed sticky column
      {
        field: 'fixedColumn',
        headerName: '',
        width: isMobile ? 150 : 250,
        flex: 0,
        headerClassName: pagesStandardStyles.columnHeader,
        cellClassName: pagesStandardStyles.stickyColumn,
        renderCell: params => {
          const text = params.value as string;
          if (text.includes(lang.yardRxContent.projected)) {
            const parts = text.split(lang.yardRxContent.projected);
            return (
              <span>
                {parts[0]}
                <span style={{ fontWeight: 400 }}>{lang.yardRxContent.projected}</span>
              </span>
            );
          }
          return text;
        },
      },
      // Cumulative group
      ...cumulativeColumns,
      // YTD group
      ...ytdColumns,
    ],
    [cumulativeColumns, ytdColumns, isMobile],
  );

  // Define column grouping model
  const columnGroupingModel: GridColumnGroupingModel = useMemo(() => {
    const cumulativeFields = cumulativeColumns.map(col => col.field);
    const ytdFields = ytdColumns.map(col => col.field);

    return [
      {
        groupId: 'cumulative-group',
        headerName: lang.yardRxContent.cumulativeHeader,
        children: cumulativeFields.map(field => ({ field })),
      },
      {
        groupId: 'ytd-group',
        headerName: lang.yardRxContent.ytdHeader,
        children: ytdFields.map(field => ({ field })),
      },
    ];
  }, [cumulativeColumns, ytdColumns]);

  if (isPrintView && !yardRxData) {
    return null;
  }

  return (
    <div>
      <div className={pagesStandardStyles.pageHeader}>
        <div className={pagesStandardStyles.headerContainer}>
          <div>
            <h2 className={pagesStandardStyles.pageTitle}>{lang.yardRxContent.title}</h2>
            <p className={pagesStandardStyles.pageSubtitle}>
              {lang.pageContent.datePrefix}{' '}
              {isValidApiDate(yardRxData?.asOfDate)
                ? formatLoadDate(yardRxData?.asOfDate ?? '')
                : 'N/A'}
              {!isPrintView && (
                <span className={pagesStandardStyles.viewInfoLinkWrapper}>
                  <a
                    href="#"
                    className={`${pagesStandardStyles.viewInfoLink} ${yardRxStyles.viewInfoLink}`}
                    onClick={e => {
                      e.preventDefault();
                      const contentInfo = kpiContentMap['YDND'];
                      if (contentInfo) {
                        const ContentComponent = contentInfo.component;
                        handleAndLogClick(`${SECTION_NM} - View Info`, {
                          callback: () => {
                            setDrawerContent({
                              title: contentInfo.title || 'More Information',
                              content: <ContentComponent />,
                            });
                            setIsDrawerOpen(true);
                          },
                        });
                      }
                    }}>
                    {lang.pageContent.viewInfo}
                  </a>
                </span>
              )}
            </p>
          </div>
          {!isPrintView && (
            <div className={yardRxStyles.headerControls}>
              {!isMobileDeviceCheck && (
                <>
                  <div className={yardRxStyles.radioGroup}>
                    <MuiRadio
                      isChecked={selectedView === 'order'}
                      label={lang.yardRxContent.byOrder}
                      onChange={() => setSelectedView('order')}
                      value="order"
                    />
                    <MuiRadio
                      isChecked={selectedView === 'sku'}
                      label={lang.yardRxContent.bySku}
                      onChange={() => setSelectedView('sku')}
                      value="sku"
                    />
                  </div>
                  <MuiButton
                    colors="secondary"
                    iconPosition="right"
                    iconType="launch"
                    onClick={handleNextDeliveryReport}>
                    {lang.yardRxContent.nextDeliveryReport} (
                    {yardRxData?.next90DayDeliveryReportCount ?? 'n/a'})
                  </MuiButton>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* DataGrid Section */}
      <div className={yardRxStyles.dataGridContainer}>
        {isLoading ? (
          <PerfYardRxSkeletonDataGrid isMobile={isMobile} />
        ) : !yardRxData ? (
          <div className={pagesStandardStyles.errorContent}>
            {lang.pageContent.dataUnavailableError.replace('{section}', 'Yard RX')}
          </div>
        ) : (
          <DataGrid
            rows={rowData}
            columns={columns}
            columnGroupingModel={columnGroupingModel}
            disableRowSelectionOnClick
            disableVirtualization
            disableColumnMenu
            disableColumnSorting
            disableColumnResize
            hideFooter
            autoHeight
            className={`${pagesStandardStyles.dataGrid} ${yardRxStyles.yardRxGrid}`}
            getRowClassName={params =>
              getAlternatingRowClassName(
                params,
                pagesStandardStyles.evenRow,
                pagesStandardStyles.oddRow,
              )
            }
            columnHeaderHeight={52}
            columnBufferPx={1500}
            sx={{
              // Hide bottom border on rows for cleaner appearance
              '.MuiDataGrid-row--borderBottom': {
                display: 'none',
              },
              // Set fixed width for first column header and empty group placeholder (desktop: 250px)
              // Empty group header appears above the first column when using column grouping
              '& .MuiDataGrid-columnHeader:first-of-type, & .MuiDataGrid-columnHeader--emptyGroup':
                {
                  flex: '0 0 250px',
                  width: 250,
                  minWidth: 250,
                  maxWidth: 250,
                  boxSizing: 'border-box',
                },
              // Style for Cumulative column group header (light pink background)
              '& .MuiDataGrid-columnHeader--filledGroup:nth-of-type(3)': {
                backgroundColor: '#feeded',
                color: '#000',
                borderRight: '2px solid #757575',
              },
              // Style for YTD column group header (light green background)
              '& .MuiDataGrid-columnHeader--filledGroup:nth-of-type(4)': {
                backgroundColor: '#EDF7ED',
                color: '#000',
                borderRight: '1px solid #e0e0e0',
              },
              // Set consistent height for all column headers
              '& .MuiDataGrid-columnHeaders': {
                minHeight: 52,
                maxHeight: 52,
              },
              // Hide the secondary header row (only show grouped headers)
              // This removes the row that would normally show individual column names
              '& .MuiDataGrid-columnHeaderRow:not(.MuiDataGrid-columnHeaderRow--filledGroup)': {
                display: 'none',
                height: 0,
                minHeight: 0,
                maxHeight: 0,
              },
              // Responsive styles for screens <= 850px (tablet/mobile)
              '@media (max-width: 850px)': {
                // Reduce first column width to 150px on smaller screens
                '& .MuiDataGrid-columnHeader:first-of-type, & .MuiDataGrid-columnHeader--emptyGroup':
                  {
                    flex: '0 0 150px',
                    width: 150,
                    minWidth: 150,
                    maxWidth: 150,
                    boxSizing: 'border-box',
                  },
                // Match cell width to header width for first column
                '& .MuiDataGrid-cell--textLeft': {
                  width: 150,
                  minWidth: 150,
                  maxWidth: 150,
                },
              },
              // Spread utility styles for sticky column behavior and group header styling
              // These must remain in sx as they use dynamic field references
              ...getGroupColumnHeaderStyles(lastGroupOneField),
              ...getDataGridStyles('fixedColumn'),
            }}
          />
        )}
      </div>

      {/* MuiDrawer — hidden in print view */}
      {!isPrintView && (
        <MuiDrawer
          anchor="right"
          headerText={drawerContent.title}
          onClose={() => setIsDrawerOpen(false)}
          open={isDrawerOpen}>
          {drawerContent.content}
        </MuiDrawer>
      )}
    </div>
  );
};

export default YardRx;
